// CMS Layout Component
import React from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useCMS } from './CMSContext';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, Users, CreditCard, Ticket, FileText, 
  Settings, LogOut, Shield, Menu, X, ChevronDown, Bell, BarChart3, Lock,
  Sun, Moon, Building2, Gift, TrendingUp, ExternalLink, 
  Newspaper, Mail, MapPin, Star, Plane, MessageSquare, Sparkles, Clock, Image as ImageIcon, Bot, DollarSign
} from 'lucide-react';

const CMSLayout = () => {
  const { admin, logout, hasRole, cmsTheme, toggleCmsTheme } = useCMS();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/cms/login');
  };

  const navItems = [
    { path: '/cms/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['super_admin', 'support', 'finance', 'read_only', 'account_manager'], permKey: 'dashboard' },
    { path: '/cms/users', icon: Users, label: 'Users', roles: ['super_admin', 'support', 'read_only', 'account_manager'], permKey: 'users' },
    { path: '/cms/passes', icon: Ticket, label: 'Passes', roles: ['super_admin', 'support', 'read_only', 'account_manager'], permKey: 'passes' },
    { path: '/cms/payments', icon: CreditCard, label: 'Payments', roles: ['super_admin', 'finance', 'read_only', 'account_manager'], permKey: 'payments' },
    { path: '/cms/b2b', icon: Building2, label: 'B2B Orders', roles: ['super_admin', 'finance', 'account_manager'], permKey: 'b2b' },
    { path: '/cms/commissions', icon: DollarSign, label: 'Commissions', roles: ['super_admin', 'finance', 'account_manager'], permKey: 'commissions' },
    { path: '/cms/reports', icon: FileText, label: 'Reports', roles: ['super_admin', 'finance', 'read_only'] },
    { path: '/cms/analytics', icon: BarChart3, label: 'Analytics', roles: ['super_admin', 'finance', 'read_only'] },
    { path: '/cms/marketing', icon: TrendingUp, label: 'Marketing', roles: ['super_admin'] },
    { path: '/cms/fraud-rules', icon: Shield, label: 'Fraud Rules', roles: ['super_admin'] },
    { path: '/cms/tc-kimlik-audit', icon: Shield, label: 'TC Kimlik Audit', roles: ['super_admin'] },
    { path: '/cms/system-health', icon: Shield, label: 'System Health', roles: ['super_admin'] },
    { path: '/cms/audit-logs', icon: FileText, label: 'Audit Logs', roles: ['super_admin'] },
    { path: '/cms/admins', icon: Settings, label: 'Admin Users', roles: ['super_admin'] },
  ];

  const filteredNavItems = navItems.filter(item => {
    // Role check first
    if (!hasRole(...item.roles)) return false;
    // For managers: check cms_permissions if item has a permKey
    if (admin?.is_manager && item.permKey) {
      const perms = admin.cms_permissions || ['dashboard', 'b2b', 'commissions'];
      return perms.includes(item.permKey);
    }
    return true;
  });

  // Admin Tools - direct links to specific admin panel tabs (super_admin only)
  const adminToolLinks = [
    { tab: 'settings', icon: Settings, label: 'Site Settings' },
    { tab: 'news', icon: Newspaper, label: 'News' },
    { tab: 'ai-blog', icon: Bot, label: 'AI Blog' },
    { tab: 'ai-images', icon: ImageIcon, label: 'AI Images' },
    { tab: 'newsletter', icon: Mail, label: 'Newsletter' },
    { tab: 'destinations', icon: MapPin, label: 'Destinations' },
    { tab: 'lastminute', icon: Clock, label: 'Last Minute' },
    { tab: 'bookings', icon: Ticket, label: 'Bookings' },
    { tab: 'testimonials', icon: MessageSquare, label: 'Testimonials' },
    { tab: 'tripadvisor', icon: Star, label: 'TripAdvisor' },
    { tab: 'transfers', icon: Plane, label: 'Transfers' },
    { tab: 'translations', icon: FileText, label: 'Translations' },
    { tab: 'popup', icon: Sparkles, label: 'Warmup Popup' },
    { tab: 'templates', icon: Mail, label: 'Email Templates' },
    { tab: 'media-library', icon: ImageIcon, label: 'Media Library' },
  ];

  const [showAdminTools, setShowAdminTools] = React.useState(false);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const roleColors = {
    super_admin: cmsTheme === 'dark' ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600',
    support: cmsTheme === 'dark' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600',
    finance: cmsTheme === 'dark' ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600',
    account_manager: cmsTheme === 'dark' ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600',
    read_only: cmsTheme === 'dark' ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-600'
  };

  // Theme-aware classes
  const themeClasses = {
    bg: cmsTheme === 'dark' ? 'bg-slate-900' : 'bg-gray-50',
    text: cmsTheme === 'dark' ? 'text-white' : 'text-gray-900',
    sidebar: cmsTheme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
    sidebarHover: cmsTheme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-gray-100',
    sidebarActive: cmsTheme === 'dark' ? 'bg-primary/20 text-primary border-l-primary' : 'bg-blue-50 text-blue-600 border-l-blue-600',
    header: cmsTheme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
    textMuted: cmsTheme === 'dark' ? 'text-slate-400' : 'text-gray-500',
    card: cmsTheme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
  };

  return (
    <div className={`min-h-screen ${themeClasses.bg} ${themeClasses.text}`} data-cms-theme={cmsTheme}>
      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-50 ${themeClasses.header} border-b px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <span className="font-semibold">Freestays CMS</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleCmsTheme} data-testid="mobile-theme-toggle">
            {cmsTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <span className={`text-xs px-2 py-1 rounded ${roleColors[admin?.role] || ''}`}>
            {admin?.role?.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-40 h-full ${themeClasses.sidebar} border-r transition-all duration-300
        ${sidebarOpen ? 'w-64' : 'w-20'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className={`h-16 flex items-center justify-between px-4 border-b ${cmsTheme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
          {sidebarOpen && (
            <Link to="/cms/dashboard" className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-primary" />
              <span className="font-bold text-lg">Freestays CMS</span>
            </Link>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-90' : '-rotate-90'}`} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
          {filteredNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                ${isActive(item.path) 
                  ? 'bg-primary text-primary-foreground' 
                  : `${themeClasses.sidebarHover} ${themeClasses.textMuted}`
                }
              `}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </Link>
          ))}

          {/* Admin Tools Section - super_admin only */}
          {hasRole('super_admin') && sidebarOpen && (
            <>
              <div className={`pt-3 mt-3 border-t ${cmsTheme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => setShowAdminTools(!showAdminTools)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium ${themeClasses.sidebarHover} ${themeClasses.textMuted}`}
                  data-testid="admin-tools-toggle"
                >
                  <span className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Admin Tools
                  </span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${showAdminTools ? 'rotate-180' : ''}`} />
                </button>
              </div>
              {showAdminTools && (
                <div className="space-y-0.5 pl-1">
                  {adminToolLinks.map((tool) => (
                    <a
                      key={tool.tab}
                      href={`/admin/dashboard?tab=${tool.tab}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-colors text-sm ${themeClasses.sidebarHover} ${themeClasses.textMuted}`}
                      data-testid={`admin-tool-${tool.tab}`}
                    >
                      <tool.icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{tool.label}</span>
                      <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </nav>

        {/* Theme Toggle & User Info */}
        <div className={`absolute bottom-0 left-0 right-0 p-4 border-t ${cmsTheme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
          {/* Theme Toggle Button */}
          <Button 
            variant="outline" 
            className={`${sidebarOpen ? 'w-full mb-3' : 'w-12 mb-3'} justify-center ${cmsTheme === 'dark' ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-100'}`}
            onClick={toggleCmsTheme}
            data-testid="theme-toggle"
          >
            {cmsTheme === 'dark' ? (
              <>
                <Sun className="w-4 h-4" />
                {sidebarOpen && <span className="ml-2">Light Mode</span>}
              </>
            ) : (
              <>
                <Moon className="w-4 h-4" />
                {sidebarOpen && <span className="ml-2">Dark Mode</span>}
              </>
            )}
          </Button>
          
          {sidebarOpen && (
            <div className="mb-3">
              <p className="font-medium truncate">{admin?.first_name} {admin?.last_name}</p>
              <p className={`text-sm ${themeClasses.textMuted} truncate`}>{admin?.email}</p>
              <span className={`inline-block text-xs px-2 py-0.5 rounded mt-1 ${roleColors[admin?.role] || ''}`}>
                {admin?.role?.replace('_', ' ')}
              </span>
            </div>
          )}
          <Button 
            variant="ghost" 
            className={`${sidebarOpen ? 'w-full' : 'w-12'} justify-start ${themeClasses.textMuted} ${themeClasses.sidebarHover}`}
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        transition-all duration-300 pt-16 lg:pt-0
        ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
      `}>
        <div className="p-6">
          <Outlet context={{ cmsTheme, themeClasses }} />
        </div>
      </main>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default CMSLayout;
